import { useSearchParams } from 'react-router-dom';

import { useQuery } from '../../../hooks/useQuery';
import { optionsToString } from '../../../reports/util/queryToFilter';
import type { InvoiceGroupOption } from '../../features/InvoiceGroupsFilter';
import type { RidersQueryParams } from '../../types';
import type { useRidersQuery } from '../useRidersQuery';

import { parseInvoiceGroupIds } from './parseInvoiceGroupIds';

interface UseRidersUrlSyncOptions
  extends Pick<ReturnType<typeof useRidersQuery>, 'queryParams' | 'handleQuery' | 'setActiveFilters'> {
  enableRidersAdvancedSorting: boolean;
  selectedInvoiceGroups: InvoiceGroupOption[];
  onStatusChange: (state: RidersQueryParams['state']) => void;
}

export const useRidersUrlSync = ({
  queryParams,
  handleQuery,
  setActiveFilters,
  enableRidersAdvancedSorting,
  selectedInvoiceGroups,
  onStatusChange,
}: UseRidersUrlSyncOptions) => {
  const [, setSearchParams] = useSearchParams();

  useQuery(
    {
      query: queryParams.query,
      state: queryParams.state,
      invoiceGroups: enableRidersAdvancedSorting
        ? queryParams.invoiceGroupIds || undefined
        : optionsToString(selectedInvoiceGroups),
      categories: queryParams.categories?.join(',') || undefined,
      safetySettings: queryParams.safetySettings?.join(',') || undefined,
      services: queryParams.services?.join(',') || undefined,
    },
    (init) => {
      const invoiceGroups = init.get('invoiceGroups');
      const invoiceGroupIds = parseInvoiceGroupIds(invoiceGroups);
      const normalizedInvoiceGroups = invoiceGroupIds.join(',') || null;

      handleQuery(init.get('query'), {
        state: init.get('state'),
        invoiceGroups: enableRidersAdvancedSorting ? normalizedInvoiceGroups : invoiceGroups,
      });
      // useQuery finishes initialization when query state matches the URL. Normalize both
      // together so legacy links can still be updated when filters are applied or cleared.
      if (enableRidersAdvancedSorting && invoiceGroups !== normalizedInvoiceGroups) {
        const normalizedParams = new URLSearchParams(init);

        if (normalizedInvoiceGroups) {
          normalizedParams.set('invoiceGroups', normalizedInvoiceGroups);
        } else {
          normalizedParams.delete('invoiceGroups');
        }
        setSearchParams(normalizedParams, { replace: true });
      }
      onStatusChange(init.get('state') || 'active');
      const categories = init.get('categories');
      const safetySettings = init.get('safetySettings');
      const services = init.get('services');

      if (categories || safetySettings || services || (enableRidersAdvancedSorting && invoiceGroups)) {
        setActiveFilters({
          categories: categories ? categories.split(',') : [],
          safetySettings: safetySettings ? safetySettings.split(',') : [],
          services: services ? services.split(',') : [],
          invoiceGroups: enableRidersAdvancedSorting ? invoiceGroupIds : [],
        });
      }
    }
  );
};
